import { useMutation, useQuery, useQueryClient } from "react-query";
import useFeedback from "../components/feedback/useFeedback";
import { ESOCIAL_JT_SERVICE_URL } from "../shared/env";
import createApi, { createQueryFetcher } from "./api";

export const api = createApi(ESOCIAL_JT_SERVICE_URL);
const queryFetcher = createQueryFetcher(api);
const REFRESH_INTERVAL = 5000;
const PAGE_SIZE = 25;

export function buildQuery(path, params = {}) {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") {
      return;
    }

    if (Array.isArray(value)) {
      if (value.length) {
        searchParams.set(key, value.join());
      }
      return;
    }

    searchParams.set(key, value);
  });

  const query = searchParams.toString();
  return query ? `${path}?${query}` : path;
}

export function useOcorrencias() {
  return useQuery(`/ocorrencias/dados-basicos`, queryFetcher, {
    refetchInterval: REFRESH_INTERVAL
  });
}

export function useOcorrenciasPaginado(
  page = 0,
  estados = [],
  expressao = "",
  tipo = "",
  incluirArquivados = false,
  cpf = ""
) {
  const query = buildQuery("/ocorrencias/paginado", {
    page,
    size: PAGE_SIZE,
    estados,
    expressao,
    tipos: tipo,
    incluirArquivados,
    cpf
  });
  return useQuery(query, queryFetcher, {
    refetchInterval: REFRESH_INTERVAL,
    keepPreviousData: true
  });
}

export function useDetalheOcorrencia(id) {
  return useQuery(`/ocorrencias/${id}`, queryFetcher, {
    refetchInterval: REFRESH_INTERVAL
  });
}

export function useTiposEnviados() {
  return useQuery(`/ocorrencias/tipos`, queryFetcher);
}

export function useLimparProducaoRestrita() {
  const queryClient = useQueryClient();
  return useMutation(() => api.post(`/producao-restrita/acoes/limpar`), {
    onSuccess: () => queryClient.invalidateQueries()
  });
}

export function useESocialJTStatus(enabled = true) {
  return useQuery(`/actuator/health`, queryFetcher, { enabled, staleTime: 0 });
}

export function useESocialGovStatus(enabled = true) {
  return useQuery(`/actuator/esocialhealth`, queryFetcher, {
    enabled,
    staleTime: 0
  });
}

export function useArquivar() {
  const queryClient = useQueryClient();
  const { showSuccess } = useFeedback();
  return useMutation((id) => api.post(`/ocorrencias/${id}/acoes/arquivar`), {
    onSuccess: () =>
      queryClient
        .invalidateQueries()
        .then(() => showSuccess(`Evento arquivado`))
  });
}

export function useDesarquivar() {
  const queryClient = useQueryClient();
  const { showInfo } = useFeedback();
  return useMutation((id) => api.post(`/ocorrencias/${id}/acoes/desarquivar`), {
    onSuccess: () =>
      queryClient
        .invalidateQueries()
        .then(() => showInfo(`Evento desarquivado`))
  });
}

export function useEnviarExclusao() {
  return useMutation((id) => api.post(`/ocorrencias/${id}/acoes/excluir`));
}

export function useEnviarExclusaoLista() {
  const queryClient = useQueryClient();
  return useMutation(
    (ids) => api.post(`/ocorrencias/acoes/excluir-lista`, ids),
    {
      onSuccess: () => queryClient.invalidateQueries()
    }
  );
}

export function useAutomacaoStatus() {
  return useQuery(`/automacao`, queryFetcher, {
    refetchInterval: REFRESH_INTERVAL
  });
}

export function useConfigurarAutomacao() {
  const queryClient = useQueryClient();
  return useMutation(
    ({ habilitado, periodo }) =>
      api.post(
        buildQuery("/automacao", {
          habilitado,
          periodo: periodo || undefined
        })
      ),
    {
      onSuccess: () => queryClient.invalidateQueries(`/automacao`)
    }
  );
}

export function useConsultarRet() {
  return useMutation(({ tipo, cpf }) =>
    api.get(`/ret/${tipo}/${cpf}`).then((res) => res.data)
  );
}

export function useConsultarLote() {
  return useMutation((protocolo) =>
    api
      .get(`/lote/consulta/${encodeURIComponent(protocolo)}`)
      .then((res) => res.data)
  );
}

export function useAtualizarProcessamentoLotes() {
  const queryClient = useQueryClient();
  return useMutation(
    (protocolo) => {
      const path = protocolo
        ? `/lote/acoes/atualizar-processamento/${encodeURIComponent(protocolo)}`
        : `/lote/acoes/atualizar-processamento`;
      return api.post(path).then((res) => res.data);
    },
    {
      onSuccess: () => queryClient.invalidateQueries()
    }
  );
}

export function useConsultarTotalizador() {
  return useMutation(({ tipo, perApuracao }) =>
    api
      .get(`/eventos/totalizadores/${tipo}/${encodeURIComponent(perApuracao)}`)
      .then((res) => res.data)
  );
}

export function useEnviarEventos() {
  const queryClient = useQueryClient();
  return useMutation(
    (filtros) =>
      api
        .post(buildQuery("/eventos/acoes/enviar", filtros))
        .then((res) => res.data),
    {
      onSuccess: () => queryClient.invalidateQueries()
    }
  );
}

export function useForcarEstadoEventos() {
  const queryClient = useQueryClient();
  return useMutation(
    ({ filtros, estadoDestino }) =>
      api
        .post(
          buildQuery("/eventos/acoes/forcar-estado", filtros),
          estadoDestino
        )
        .then((res) => res.data),
    {
      onSuccess: () => queryClient.invalidateQueries()
    }
  );
}

export function useAbortarProcessamentoEventos() {
  const queryClient = useQueryClient();
  return useMutation(
    () =>
      api.post(`/eventos/acoes/abortar-processamento`).then((res) => res.data),
    {
      onSuccess: () => queryClient.invalidateQueries()
    }
  );
}

export function useSolicitarDownloadPorNrRecibo() {
  return useMutation((payload) =>
    api
      .post(`/download-eventos/acoes/solicitar-por-nr-recibo`, payload)
      .then((res) => res.data)
  );
}

export function useSolicitarDownloadPorId() {
  return useMutation((payload) =>
    api
      .post(`/download-eventos/acoes/solicitar-por-id`, payload)
      .then((res) => res.data)
  );
}

export function useConsultarIdentificadoresTrabalhador() {
  return useMutation((payload) =>
    api
      .post(
        `/download-eventos/acoes/consultar-identificadores-trabalhador`,
        payload
      )
      .then((res) => res.data)
  );
}

export function useRecuperarTotalizadoresFaltantes() {
  const queryClient = useQueryClient();
  return useMutation(
    (payload) =>
      api
        .post(
          `/download-eventos/acoes/recuperar-totalizadores-faltantes`,
          payload
        )
        .then((res) => res.data),
    { onSuccess: () => queryClient.invalidateQueries() }
  );
}

export function usePainelOcorrencias(filtros) {
  return useQuery(buildQuery("/painel/ocorrencias", filtros), queryFetcher, {
    keepPreviousData: true,
    refetchInterval: 30000
  });
}
export function useDashboard(filtros) {
  return useQuery(buildQuery("/painel/dashboard", filtros), queryFetcher, {
    refetchInterval: 60000
  });
}
export function useIdentidade(id) {
  return useQuery(`/painel/ocorrencias/${id}/identidade`, queryFetcher, {
    enabled: Boolean(id),
    staleTime: 60000
  });
}
export function useTotalizadores(filtros) {
  return useQuery(buildQuery("/painel/totalizadores", filtros), queryFetcher, {
    enabled: Boolean(filtros.competencia)
  });
}
export function useFolhaResumo() {
  return useQuery("/sincronizacao-folha/resumo", queryFetcher, {
    refetchInterval: 30000
  });
}
export function useFolhaPendencias(filtros) {
  return useQuery(
    buildQuery("/sincronizacao-folha/pendencias/paginado", filtros),
    queryFetcher,
    { keepPreviousData: true, refetchInterval: 15000 }
  );
}
export function useFolhaPendencia(id) {
  return useQuery(`/sincronizacao-folha/pendencias/${id}`, queryFetcher, {
    enabled: Boolean(id),
    refetchInterval: 15000
  });
}
export function useReagendarFolha() {
  const client = useQueryClient();
  return useMutation(
    (id) =>
      api
        .post(`/sincronizacao-folha/pendencias/${id}/acoes/reagendar`)
        .then((r) => r.data),
    { onSuccess: () => client.invalidateQueries() }
  );
}
